import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Switch,
  Row,
  Col,
  Divider
} from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  TagsOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import {
  createUdhyamCategoryRequest,
  updateUdhyamCategoryRequest
} from '../../../redux/actions/udhyamCategoryActions';
import '../../../styles/forms.css';

const UdhyamCategoryForm = ({ onCancel }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { editingCategory, creating, updating } = useSelector(state => state.udhyamCategories);

  const isEditing = !!editingCategory;
  const isSubmitting = creating || updating;

  useEffect(() => {
    if (editingCategory) {
      form.setFieldsValue({
        category_name: editingCategory.category_name,
        is_active: editingCategory.is_active !== undefined ? editingCategory.is_active : true
      });
    } else {
      form.setFieldsValue({
        is_active: true
      });
    }
  }, [editingCategory, form]);

  const handleSubmit = (values) => {
    const categoryData = {
      category_name: values.category_name.trim(),
      is_active: values.is_active !== undefined ? values.is_active : true
    };

    if (isEditing) {
      dispatch(updateUdhyamCategoryRequest(editingCategory.id, categoryData));
    } else {
      dispatch(createUdhyamCategoryRequest(categoryData));
    }

    // Close modal after successful submission (handled in saga)
    onCancel();
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Validation rules
  const validateCategoryName = (_, value) => {
    if (!value || !value.trim()) {
      return Promise.reject(new Error('Category name is required'));
    }
    if (value.trim().length < 2) {
      return Promise.reject(new Error('Category name must be at least 2 characters'));
    }
    if (value.trim().length > 100) {
      return Promise.reject(new Error('Category name must not exceed 100 characters'));
    }
    // Check for valid characters (letters, numbers, spaces, hyphens, parentheses)
    if (!/^[a-zA-Z0-9\s\-()&.]+$/.test(value.trim())) {
      return Promise.reject(new Error('Category name contains invalid characters'));
    }
    return Promise.resolve();
  };

  return (
    <div className="professional-form">
      {/* Form Header */}
      <div className="form-modal-header">
        <div className="form-modal-icon">
          <TagsOutlined />
        </div>
        <div className="form-modal-title">
          <h3>{isEditing ? 'Edit Udhyam Category' : 'New Udhyam Category'}</h3>
          <p>Define business classification categories for vendors</p>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark="optional"
      >
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              label="Category Name"
              name="category_name"
              rules={[{ validator: validateCategoryName }]}
              tooltip="Enter a unique Udhyam category name"
            >
              <Input
                placeholder="e.g., Micro Enterprise, Small Enterprise, Medium Enterprise"
                maxLength={100}
                showCount
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: '1rem 0' }} />

        <Row gutter={16}>
          <Col xs={24}>
            <div className="status-badge-group">
              <div className="status-badge-item">
                <span className="status-badge-label">Status</span>
                <Form.Item
                  name="is_active"
                  valuePropName="checked"
                  style={{ margin: 0 }}
                >
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                  />
                </Form.Item>
              </div>
              <div className="input-help-text">
                <InfoCircleOutlined />
                Inactive categories won't be available for selection
              </div>
            </div>
          </Col>
        </Row>

        <div className="form-actions">
          <Button onClick={handleCancel} size="large">
            <CloseOutlined />
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            icon={<SaveOutlined />}
            size="large"
          >
            {isEditing ? 'Update Category' : 'Create Category'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default UdhyamCategoryForm;